import React from 'react'
import { colors } from './fieldTheme'
import { formatDateTime } from './fieldDates'
import { authorOf } from './fieldSummary'
import { shortName } from './fieldLabels'
import { canEditEntry } from './fieldActivity'
import { ChevronLeft, CheckIcon, AlertIcon, InfoIcon, PencilIcon, GalleryIcon, WrenchIcon } from './FieldIcons'
import { FlowChip, SectionTitle, StickyActionBar, PrimaryButton } from './FieldUI'

// Inventaire rouvert depuis Mon activité (canvas A4) : tel qu'il a été enregistré. Je modifie mes propres
// inventaires, jamais ceux des autres (décisions 10 et 31) ; une modification crée une nouvelle version.

const TYPE_TITLES = { arrival: 'Inventaire d\'entrée', departure: 'Inventaire de sortie', free: 'Inventaire hors séjour', tour: 'Inventaire du tour' }
const TONES = { ok: { background: '#DCFCE7', color: '#14532D', circle: '#15803D' }, gap: { background: '#FEE2E2', color: '#7F1D1D', circle: '#B91C1C' } }

function Lines({ item, badge })
{
	return (
		<div style={{ minHeight: 104, display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px' }}>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
				<span style={{ fontSize: 19, fontWeight: 800, overflowWrap: 'break-word' }}>{item.name}</span>
				<span style={{ fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{item.line}</span>
				<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{item.theme}</span>
			</span>
			{badge}
		</div>
	)
}

function Group({ children })
{
	const items = React.Children.toArray(children)
	return (
		<div style={{ margin: '0 20px', background: colors.surface, borderRadius: 18, overflow: 'hidden' }}>
			{items.map((child, index) => <div key={child.key || index} style={{ borderTop: index > 0 ? '1px solid ' + colors.separator : 0 }}>{child}</div>)}
		</div>
	)
}

// followUps : interventions et commentaires ajoutés après l'enregistrement (journal d'activité)
export default function FieldActivityInventory({ entry, followUps, user, now, onBack, onEdit })
{
	const snap = entry.snapshot
	const comments = snap.comments.concat(followUps ? followUps.comments : [])
	const interventions = snap.interventions.concat(followUps ? followUps.interventions : [])
	const mine = user && snap.user === user._id
	const by = mine ? 'par vous' + (shortName(snap.userName) ? ' (' + shortName(snap.userName) + ')' : '') : 'par ' + (shortName(snap.userName) || 'un collègue')
	const identity = [snap.guest, snap.stayNumber ? 'séjour n° ' + snap.stayNumber : null].filter(Boolean).join(', ')
	const tone = snap.banner ? TONES[snap.banner.tone] : null

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 56, padding: '4px 20px 0' }}>
				<button
					type="button"
					className="field-solid"
					onClick={onBack}
					style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 18px 0 10px', borderRadius: 999, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', flex: 'none' }}
				>
					<ChevronLeft />
					Mon activité
				</button>
				<FlowChip type={snap.type} location={snap.location} />
			</div>
			<div style={{ padding: '24px 20px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
				<h1 style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2, color: colors.text, paddingBottom: 2 }}>{TYPE_TITLES[snap.type] || 'Inventaire'}</h1>
				{identity && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{identity.charAt(0).toUpperCase() + identity.slice(1)}</span>}
				<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{(snap.version > 1 ? 'Version ' + snap.version + ' du ' : 'Version du ') + formatDateTime(snap.time, now) + ', ' + by}</span>
			</div>

			{snap.banner && (
				<div role="status" style={{ margin: '24px 20px 0', display: 'flex', alignItems: 'center', gap: 14, padding: 20, borderRadius: 18, background: tone.background, color: tone.color }}>
					<span aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 999, background: tone.circle, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
						{snap.banner.tone === 'ok' ? <CheckIcon size={28} color="#FFFFFF" /> : <AlertIcon />}
					</span>
					<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
						<span style={{ fontSize: 24, fontWeight: 800 }}>{snap.banner.title}</span>
						<span style={{ fontSize: 17, fontWeight: 700 }}>{snap.banner.text}</span>
					</span>
				</div>
			)}
			{snap.referenceLabel && <p style={{ padding: '12px 20px 0', fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{snap.referenceLabel}</p>}

			{snap.gaps.length > 0 && (
				<React.Fragment>
					<SectionTitle>{snap.gapsTitle + ' (' + snap.gaps.length + ')'}</SectionTitle>
					<Group>
						{snap.gaps.map(item => <Lines key={item.theme + item.name} item={item} badge={<span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', minHeight: 34, padding: '0 12px', borderRadius: 999, background: '#FEE2E2', color: '#7F1D1D', fontSize: 15, fontWeight: 800 }}>{item.badge}</span>} />)}
					</Group>
					{snap.othersText && <p style={{ padding: '12px 20px 0', fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{snap.othersText}</p>}
				</React.Fragment>
			)}

			{snap.missing.length > 0 && (
				<React.Fragment>
					<SectionTitle>{snap.missingTitle + ' (' + snap.missing.length + ')'}</SectionTitle>
					<Group>
						{snap.missing.map(item => <Lines key={item.theme + item.name} item={item} badge={<span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 34, padding: '0 12px 0 8px', borderRadius: 999, background: '#FFEDD5', color: '#7C2D12', fontSize: 15, fontWeight: 800 }}><InfoIcon size={18} />{item.badge}</span>} />)}
					</Group>
				</React.Fragment>
			)}

			{comments.length > 0 && (
				<React.Fragment>
					<SectionTitle>{'Commentaires (' + comments.length + ')'}</SectionTitle>
					<div style={{ margin: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
						{comments.map((comment, index) => {
							const author = authorOf(comment.user, user)
							return (
								<div key={index} style={{ background: colors.surface, borderRadius: 18, display: 'flex', gap: 12, padding: 20 }}>
									<span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, background: colors.primary, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flex: 'none' }}>{author.initials}</span>
									<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
										<span style={{ fontSize: 17, fontWeight: 700 }}>{author.name}</span>
										<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{[formatDateTime(comment.time, now), comment.theme].filter(Boolean).join(', ')}</span>
										<p style={{ fontSize: 17, marginTop: 4, overflowWrap: 'break-word', whiteSpace: 'pre-line' }}>{comment.text}</p>
									</div>
								</div>
							)
						})}
					</div>
				</React.Fragment>
			)}

			{snap.photos.length > 0 && (
				<React.Fragment>
					<SectionTitle>{'Photos (' + snap.photos.length + ')'}</SectionTitle>
					<div style={{ margin: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
						{snap.photos.map((photo, index) => (
							<div key={index} style={{ background: colors.surface, borderRadius: 18, padding: 16, display: 'flex', gap: 14, alignItems: 'center' }}>
								<span aria-hidden="true" style={{ width: 72, height: 72, borderRadius: 14, background: '#DDE1EA', display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textSecondary, flex: 'none' }}><GalleryIcon size={30} /></span>
								<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
									<span style={{ fontSize: 17, fontWeight: 600, color: photo.description ? colors.text : colors.textSecondary }}>{photo.description || 'Sans description'}</span>
									<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{[photo.theme, formatDateTime(photo.time, now)].filter(Boolean).join(', ')}</span>
								</span>
							</div>
						))}
					</div>
				</React.Fragment>
			)}

			{interventions.length > 0 && (
				<React.Fragment>
					<SectionTitle>{'Suite donnée (' + interventions.length + ')'}</SectionTitle>
					<Group>
						{interventions.map((item, index) => (
							<div key={index} style={{ minHeight: 80, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px' }}>
								<span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, background: item.type === 'Ménage' ? '#1CB0C4' : '#8124FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><WrenchIcon size={24} color={item.type === 'Ménage' ? colors.text : '#FFFFFF'} /></span>
								<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
									<span style={{ fontSize: 19, fontWeight: 700 }}>{item.label}</span>
									<span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{'Intervention ' + item.type + ', ' + formatDateTime(item.time, now)}</span>
								</span>
							</div>
						))}
					</Group>
				</React.Fragment>
			)}

			<div aria-hidden="true" style={{ height: 32, flex: 'none' }} />
			<StickyActionBar>
				{canEditEntry(entry, user) && (
					<React.Fragment>
						<PrimaryButton icon={<PencilIcon />} onClick={onEdit}>Modifier mon inventaire</PrimaryButton>
						<p style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary, textAlign: 'center' }}>Vos changements créent une nouvelle version. Celle-ci reste consultable.</p>
					</React.Fragment>
				)}
				{!canEditEntry(entry, user) && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>Seule la personne qui l'a fait peut modifier cet inventaire.</p>}
			</StickyActionBar>
		</div>
	)
}
